import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, Camera, MapPin, Eye, HardDrive, Cable, DollarSign, Clock, CheckCircle, Sparkles, Home, Building2, Warehouse, ShoppingBag, Factory, Hotel, X, AlertTriangle, Zap } from 'lucide-react';

type PropertyType = 'house' | 'office' | 'warehouse' | 'factory' | 'shop' | 'hotel' | null;

interface CameraRecommendation {
  zone: string;
  model: string;
  qty: number;
  angle: string;
  note: string;
  type: 'dome' | 'bullet' | 'ptz' | 'fisheye';
}

interface PlannerResult {
  totalCameras: number;
  cameras: CameraRecommendation[];
  nvr: string;
  storage: string;
  cable: string;
  budget: { min: number; max: number };
  installDays: number;
  blindSpots: string[];
  highlights: string[];
}

const propertyTypes = [
  { id: 'house', label: 'House', icon: Home, color: 'from-blue-400 to-blue-600', desc: 'Residential' },
  { id: 'office', label: 'Office', icon: Building2, color: 'from-violet-400 to-violet-600', desc: 'Commercial' },
  { id: 'warehouse', label: 'Warehouse', icon: Warehouse, color: 'from-amber-400 to-amber-600', desc: 'Industrial' },
  { id: 'factory', label: 'Factory', icon: Factory, color: 'from-red-400 to-red-600', desc: 'Manufacturing' },
  { id: 'shop', label: 'Shop / Retail', icon: ShoppingBag, color: 'from-green-400 to-green-600', desc: 'Retail' },
  { id: 'hotel', label: 'Hotel', icon: Hotel, color: 'from-pink-400 to-rose-600', desc: 'Hospitality' },
];

function generatePlan(type: PropertyType, size: string): PlannerResult {
  const sqft = parseInt(size) || 2000;
  const sizeMultiplier = sqft / 1000;

  const plans: Record<string, PlannerResult> = {
    house: {
      totalCameras: Math.min(Math.max(4, Math.round(sizeMultiplier * 3)), 12),
      cameras: [
        { zone: 'Main Entrance / Gate', model: 'Hikvision DS-2CD2547G2-LS 4MP ColorVu', qty: 2, angle: '90° wide angle', note: 'Color 24/7, license plate capture', type: 'bullet' },
        { zone: 'Front & Back Yard', model: 'Dahua IPC-HDW3849H-AS-PV', qty: 2, angle: '110° fisheye-style', note: 'Full perimeter coverage', type: 'dome' },
        { zone: 'Living Room', model: 'Hikvision DS-2CD2347G2-LU', qty: 1, angle: '99° standard', note: 'Indoor 4MP, color night', type: 'dome' },
        { zone: 'Garage / Driveway', model: 'Dahua IPC-HFW3849S-AS-LED', qty: 1, angle: '102° wide', note: 'Vehicle detection', type: 'bullet' },
      ],
      nvr: 'Hikvision DS-7608NXI-K1/8P (8CH PoE)',
      storage: '2TB WD Purple (30-day retention)',
      cable: '80m CAT6 + 20m RG59 coaxial',
      budget: { min: 185000, max: 245000 },
      installDays: 1,
      blindSpots: ['Side alley near fence', 'Backyard corners if large'],
      highlights: ['24/7 color vision at entry', 'Mobile app remote view', 'Motion alerts to phone'],
    },
    office: {
      totalCameras: Math.min(Math.max(8, Math.round(sizeMultiplier * 5)), 24),
      cameras: [
        { zone: 'Main Entrance (Exterior)', model: 'Hikvision DS-2CD2T47G2-L 4MP', qty: 2, angle: '80° telephoto', note: 'Face detection + strobe', type: 'bullet' },
        { zone: 'Reception / Lobby', model: 'Dahua IPC-HDW3849H Panoramic', qty: 1, angle: '360°', note: 'Full room coverage, fisheye', type: 'fisheye' },
        { zone: 'Open Office Area', model: 'Hikvision DS-2CD2347G2-LU', qty: 4, angle: '99° dome', note: 'Grid placement 10m apart', type: 'dome' },
        { zone: 'Server Room', model: 'Hikvision DS-2CD2547G2-LS', qty: 1, angle: '99° dome', note: 'Critical asset protection', type: 'dome' },
        { zone: 'Parking Lot', model: 'Hikvision DS-2CD2T47G2 IR Bullet', qty: 3, angle: '60° long range', note: 'LPR capability, 60m IR', type: 'bullet' },
        { zone: 'Emergency Exits', model: 'Dahua IPC-HFW2849S-S-IL', qty: 2, angle: '80° IR', note: 'After-hours monitoring', type: 'dome' },
      ],
      nvr: 'Hikvision DS-7616NXI-K2/16P (16CH 4K NVR)',
      storage: '4TB WD Purple RAID-1 (60-day retention)',
      cable: '300m CAT6 FTP + cable trays',
      budget: { min: 485000, max: 640000 },
      installDays: 3,
      blindSpots: ['Stairwell between floors 2-3', 'Underground parking entrance'],
      highlights: ['AI face detection at all entry points', 'Centralized NOC monitoring ready', 'Integration with access control possible'],
    },
    warehouse: {
      totalCameras: Math.min(Math.max(12, Math.round(sizeMultiplier * 6)), 32),
      cameras: [
        { zone: 'Loading Dock / Entry', model: 'Hikvision DS-2CD2T87G2-L 8MP', qty: 4, angle: '80° motorized zoom', note: 'Vehicle + container tracking', type: 'bullet' },
        { zone: 'Aisles (High Mount)', model: 'Dahua IPC-HFW5849T1-ASE-LED', qty: 6, angle: '104° ultra-wide', note: '10m height, 180° fan coverage', type: 'bullet' },
        { zone: 'Perimeter Fence', model: 'Hikvision DS-2CD2T47G2 IR', qty: 4, angle: '60° long range', note: 'Intrusion detection, 80m IR', type: 'bullet' },
        { zone: 'Manager Office / Cashier', model: 'Hikvision DS-2CD2327G2-LU', qty: 2, angle: '99° dome', note: 'Cash area / high-value', type: 'dome' },
        { zone: 'PTZ Overview (Ceiling)', model: 'Dahua SD49425XB-HNR PTZ', qty: 2, angle: '360° pan-tilt-zoom', note: 'Guard tour preset, 25× optical', type: 'ptz' },
      ],
      nvr: 'Dahua NVR608-64-XI (64CH AI NVR)',
      storage: '8TB WD Purple × 2 RAID-5 (90-day retention)',
      cable: '600m CAT6 + fiber backbone for long runs',
      budget: { min: 850000, max: 1200000 },
      installDays: 5,
      blindSpots: ['Behind tall racking units', 'Emergency stairwells'],
      highlights: ['Forklift & vehicle detection', 'Theft & perimeter breach alerts', 'Remote PTZ patrol mode'],
    },
    factory: {
      totalCameras: Math.min(Math.max(16, Math.round(sizeMultiplier * 7)), 48),
      cameras: [
        { zone: 'Production Line', model: 'Hikvision DS-2CD2T87G2-L 8MP', qty: 8, angle: '80° overview', note: 'Production monitoring, QC', type: 'bullet' },
        { zone: 'Hazardous Zones', model: 'Dahua IPC-HFW5449T-SE Explosion Proof', qty: 4, angle: '99° IP67', note: 'Explosion-proof rated', type: 'dome' },
        { zone: 'Control Room / HMI', model: 'Hikvision DS-2CD2H47G2-IZHS', qty: 2, angle: '128° varifocal', note: 'Operator accountability', type: 'dome' },
        { zone: 'Raw Materials Storage', model: 'Dahua IPC-HDW3849H-AS-PV', qty: 4, angle: '110° color', note: '24/7 color, inventory visible', type: 'dome' },
        { zone: 'Roof / Perimeter', model: 'Hikvision DS-2CD2T25G1 IR Bullet', qty: 4, angle: '60° long range', note: 'Perimeter security', type: 'bullet' },
        { zone: 'Emergency/Fire Exits', model: 'Dahua IPC-HFW2849S', qty: 4, angle: '80° IR', note: 'Safety compliance', type: 'dome' },
      ],
      nvr: 'Hikvision DS-96128NI-I24 (128CH Enterprise)',
      storage: '12TB HDD × 4 RAID-6 (180-day retention)',
      cable: '1200m CAT6 + 300m Fiber (long factory floor)',
      budget: { min: 1800000, max: 2800000 },
      installDays: 10,
      blindSpots: ['Under elevated conveyor belts', 'Behind large machinery'],
      highlights: ['PPE compliance detection', 'Heat & fire anomaly AI', 'Integration with SCADA/PLC possible'],
    },
    shop: {
      totalCameras: Math.min(Math.max(4, Math.round(sizeMultiplier * 4)), 16),
      cameras: [
        { zone: 'Entrance / Exit', model: 'Hikvision DS-2CD2547G2-LS', qty: 2, angle: '99° ColorVu', note: 'Customer counting, full color', type: 'dome' },
        { zone: 'Cashier / POS Area', model: 'Dahua IPC-HDW3849H', qty: 2, angle: '99° dome', note: 'Transaction monitoring', type: 'dome' },
        { zone: 'Display Shelves', model: 'Hikvision DS-2CD2327G2-LU', qty: 3, angle: '99° wide', note: 'Theft prevention', type: 'dome' },
        { zone: 'Storage Room', model: 'Dahua IPC-HFW2849S', qty: 1, angle: '80° IR', note: 'Inventory protection', type: 'dome' },
      ],
      nvr: 'Hikvision DS-7608NXI-K1/8P (8CH)',
      storage: '2TB WD Purple (45-day retention)',
      cable: '150m CAT6',
      budget: { min: 185000, max: 285000 },
      installDays: 1,
      blindSpots: ['Blind corners in changing rooms (not recommended to cover)'],
      highlights: ['People counting analytics', 'Queue detection at cashier', 'Heat mapping for store layout optimization'],
    },
    hotel: {
      totalCameras: Math.min(Math.max(20, Math.round(sizeMultiplier * 8)), 64),
      cameras: [
        { zone: 'Main Entrance & Lobby', model: 'Dahua SD49425XB-HNR PTZ 4MP', qty: 2, angle: '360° PTZ', note: 'Guest check-in tracking', type: 'ptz' },
        { zone: 'Corridors (each floor)', model: 'Hikvision DS-2CD2347G2-LU', qty: 8, angle: '99° dome', note: 'Every 15m along corridor', type: 'dome' },
        { zone: 'Parking & Driveway', model: 'Hikvision DS-2CD2T47G2 8MP', qty: 6, angle: '80° LPR', note: 'License plate capture', type: 'bullet' },
        { zone: 'Pool & Garden', model: 'Dahua IPC-HFW5849T-ASE-LED', qty: 4, angle: '104° color', note: 'Night swimming safety', type: 'bullet' },
        { zone: 'Reception & Front Desk', model: 'Hikvision DS-2CD2547G2-LS', qty: 2, angle: '99° ColorVu', note: 'Staff + guest monitoring', type: 'dome' },
        { zone: 'Kitchen / Back of House', model: 'Dahua IPC-HDW3849H', qty: 4, angle: '99° dome', note: 'Food safety compliance', type: 'dome' },
        { zone: 'Perimeter Wall', model: 'Hikvision DS-2CD2T25G1 IR', qty: 6, angle: '60° long range', note: 'Unauthorized access', type: 'bullet' },
      ],
      nvr: 'Dahua NVR608-32-XI (32CH AI NVR)',
      storage: '8TB WD Purple × 2 (90-day retention)',
      cable: '1500m CAT6 + cable management',
      budget: { min: 1200000, max: 1800000 },
      installDays: 7,
      blindSpots: ['Individual room corridors (no cameras inside rooms)', 'Service elevator interior'],
      highlights: ['VIP facial recognition (optional)', 'SOS alert integration', 'Central security monitoring room'],
    },
  };

  return plans[type || 'office'] || plans.office;
}

export default function AICameraPage() {
  const [selectedType, setSelectedType] = useState<PropertyType>(null);
  const [size, setSize] = useState('');
  const [uploadedFile, setUploadedFile] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<PlannerResult | null>(null);
  const [step, setStep] = useState(1);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file.name);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedType) return;
    setIsAnalyzing(true);
    await new Promise(resolve => setTimeout(resolve, 2500));
    const plan = generatePlan(selectedType, size);
    setResult(plan);
    setIsAnalyzing(false);
    setStep(3);
  };

  const reset = () => {
    setSelectedType(null);
    setSize('');
    setUploadedFile(null);
    setResult(null);
    setStep(1);
  };

  const typeColors: Record<string, string> = {
    house: 'text-blue-600', office: 'text-violet-600', warehouse: 'text-amber-600',
    factory: 'text-red-600', shop: 'text-green-600', hotel: 'text-pink-600',
  };
  const cameraIcons: Record<string, string> = { dome: '🔵', bullet: '🔴', ptz: '🟡', fisheye: '🟣' };

  return (
    <div className="h-full overflow-y-auto bg-[#FAFAFA] p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-blue-200">
            <Camera className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">AI Camera Planner</h1>
            <p className="text-xs text-gray-500">Upload floor plan · Get instant CCTV recommendations</p>
          </div>
        </div>
        {result && (
          <button onClick={reset} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-xl px-4 py-2 transition-colors">
            <X className="w-4 h-4" /> Start Over
          </button>
        )}
      </div>

      {/* Step 1 — Property Type */}
      <div className={`bg-white rounded-2xl border border-gray-200 p-6 mb-4 ${step < 1 ? 'opacity-50' : ''}`}>
        <div className="flex items-center gap-2 mb-4">
          <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">1</span>
          <h2 className="text-sm font-semibold text-gray-900">Select Property Type</h2>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {propertyTypes.map(pt => {
            const Icon = pt.icon;
            const isSelected = selectedType === pt.id;
            return (
              <button
                key={pt.id}
                onClick={() => { setSelectedType(pt.id as PropertyType); if (step < 2) setStep(2); }}
                className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all ${
                  isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-white'
                }`}
              >
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${pt.color} flex items-center justify-center shadow-sm`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <span className="text-xs font-medium text-gray-700">{pt.label}</span>
                {isSelected && <CheckCircle className="w-3 h-3 text-blue-500" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Step 2 — Details & Upload */}
      <AnimatePresence>
        {step >= 2 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-gray-200 p-6 mb-4"
          >
            <div className="flex items-center gap-2 mb-4">
              <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">2</span>
              <h2 className="text-sm font-semibold text-gray-900">Property Details & Floor Plan</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1.5">Property Size (sq ft)</label>
                <input
                  type="number"
                  value={size}
                  onChange={e => setSize(e.target.value)}
                  placeholder="e.g. 3000"
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1.5">Upload Floor Plan (Optional)</label>
                <label className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                  <Upload className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-500">{uploadedFile || 'Upload PDF, PNG, or JPG'}</span>
                  <input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={handleFileUpload} className="hidden" />
                </label>
              </div>
            </div>
            <div className="mt-4">
              <button
                onClick={handleAnalyze}
                disabled={isAnalyzing}
                className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-cyan-600 text-white text-sm font-medium px-6 py-2.5 rounded-xl hover:shadow-lg hover:shadow-blue-200 transition-all disabled:opacity-60"
              >
                {isAnalyzing ? (
                  <>
                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
                      <Sparkles className="w-4 h-4" />
                    </motion.div>
                    Analyzing with AI…
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Generate Camera Plan
                  </>
                )}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Step 3 — Results */}
      <AnimatePresence>
        {result && step === 3 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            {/* Summary */}
            <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl p-6 text-white">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5" />
                <h2 className="text-base font-semibold">AI Analysis Complete</h2>
                <span className="ml-auto text-xs bg-white/20 px-2 py-0.5 rounded-full">
                  {selectedType && propertyTypes.find(p => p.id === selectedType)?.label}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Total Cameras', value: result.totalCameras, icon: Camera },
                  { label: 'Budget (LKR)', value: `${(result.budget.min / 1000).toFixed(0)}K–${(result.budget.max / 1000).toFixed(0)}K`, icon: DollarSign },
                  { label: 'Install Days', value: result.installDays, icon: Clock },
                  { label: 'Blind Spots', value: result.blindSpots.length, icon: Eye },
                ].map((stat, i) => {
                  const Icon = stat.icon;
                  return (
                    <div key={i} className="bg-white/15 rounded-xl p-3 text-center">
                      <Icon className="w-4 h-4 mx-auto mb-1 opacity-80" />
                      <div className="text-xl font-bold">{stat.value}</div>
                      <div className="text-xs opacity-80">{stat.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Camera Placement */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-500" /> Camera Placement Plan
              </h3>
              <div className="space-y-3">
                {result.cameras.map((cam, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl hover:bg-blue-50 transition-colors">
                    <span className="text-lg mt-0.5">{cameraIcons[cam.type]}</span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900">{cam.zone}</span>
                        <span className="text-xs font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">× {cam.qty}</span>
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5">{cam.model}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-gray-400 flex items-center gap-1"><Eye className="w-3 h-3" />{cam.angle}</span>
                        <span className="text-xs text-gray-400">{cam.note}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Infrastructure */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <HardDrive className="w-4 h-4 text-violet-500" /> Storage & NVR
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Zap className="w-3.5 h-3.5 text-gray-400" /><span className="font-medium">NVR:</span> {result.nvr}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <HardDrive className="w-3.5 h-3.5 text-gray-400" /><span className="font-medium">Storage:</span> {result.storage}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Cable className="w-3.5 h-3.5 text-gray-400" /><span className="font-medium">Cabling:</span> {result.cable}
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" /> Blind Spot Analysis
                </h3>
                <div className="space-y-2">
                  {result.blindSpots.map((spot, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-gray-600 bg-amber-50 px-3 py-2 rounded-lg">
                      <AlertTriangle className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
                      {spot}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* AI Highlights */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-indigo-500" /> AI Highlights
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {result.highlights.map((h, i) => (
                  <div key={i} className="flex items-start gap-2 bg-indigo-50 rounded-xl px-3 py-2">
                    <CheckCircle className="w-3.5 h-3.5 text-indigo-500 mt-0.5 flex-shrink-0" />
                    <span className="text-xs text-gray-700">{h}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="flex items-center gap-3">
              <button className="flex items-center gap-2 bg-gradient-to-r from-[#7FA86A] to-[#96BE82] text-white text-sm font-medium px-6 py-2.5 rounded-xl hover:shadow-lg hover:shadow-[rgba(127,168,106,0.2)] transition-all">
                <FileText className="w-4 h-4" /> Generate Quotation
              </button>
              <button className="flex items-center gap-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium px-6 py-2.5 rounded-xl hover:bg-gray-50 transition-colors">
                <MapPin className="w-4 h-4" /> Export PDF Report
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
